import type { BoardPower } from "../data/model";
import { powers as P } from "../data/tuning";
import type { Inventory, PowerLevels, SaveData } from "../core/SaveManager";

/** Hover boards: the original's board powers, our names. Prices ⚠ ours, in the original's range. */
export interface BoardDefinition {
  id: string;
  name: string;
  price: number;
  keys?: number;
  powers: BoardPower[];
  color: string;
  blurb: string;
}

export const boards: BoardDefinition[] = [
  { id: "default", name: "Hover Board", price: 0, powers: [], color: "#9aa3ad", blurb: "Saves you from one crash. 30 seconds." },
  { id: "lowrider", name: "Lowrider", price: 15000, powers: ["stayLow"], color: "#e4393c", blurb: "Rides low: bars pass over you." },
  { id: "bouncer", name: "Bouncer", price: 15000, powers: ["superJump"], color: "#f4c04e", blurb: "Twice the hop." },
  { id: "drifter", name: "Drifter", price: 20000, powers: ["speed"], color: "#3b7ddd", blurb: "A little extra speed." },   // the original's plain Speed power (smoothDrift = the glide, which Glider has)
  { id: "rocket", name: "Rocket", price: 25000, powers: ["superSpeed"], color: "#ff8a3c", blurb: "Faster. Much faster." },
  { id: "double", name: "Double", price: 25000, powers: ["doubleJump"], color: "#8fd26b", blurb: "A second hop in the air." },
  { id: "zapper", name: "Zapper", price: 30000, powers: ["zapSideways"], color: "#9945ff", blurb: "Teleports between lanes." },
  { id: "glider", name: "Glider", price: 30000, powers: ["glider"], color: "#7fd8c9", blurb: "Hangs in the air after a hop." },
  { id: "baltic", name: "Baltic", price: 0, keys: 50, powers: ["superJump", "smoothDrift"], color: "#14f195", blurb: "High hops, then a long glide." },
  { id: "excel", name: "Excel", price: 0, keys: 80, powers: ["superSpeed", "stayLow"], color: "#1d7044", blurb: "Low and fast. Cells not responding." }
];

export const boardById = (id: string): BoardDefinition => boards.find((b) => b.id === id) ?? boards[0];

export type Consumable = "headstart" | "booster" | "shield" | "board" | "mysteryBox";

/** Consumables ⚠ prices from the fan wiki where it has them (headstart 2,000 · booster 3,000 · board 300 · box 500), shield ours. */
export const consumables: { id: Consumable; name: string; price: number; blurb: string }[] = [
  { id: "headstart", name: "Headstart", price: 2000, blurb: "Fly the first 300 m." },
  { id: "booster", name: "Score Booster", price: 3000, blurb: "+5 to +7 multiplier for the run." },
  { id: "shield", name: "Shield", price: 1500, blurb: "One free crash." },
  { id: "board", name: "Hover Board", price: 300, blurb: "+1 board." },
  { id: "mysteryBox", name: "Mystery Box", price: 500, blurb: "SOL, keys, boards, boosters…" }
];

export const inventoryKey: Record<Consumable, keyof Inventory> = { headstart: "headstarts", booster: "boosters", shield: "shields", board: "boards", mysteryBox: "mysteryBoxes" };

export const upgradeNames: Record<keyof PowerLevels, string> = { magnet: "Magnet", x2: "2X Score", sneakers: "Super Springs", jetpack: "Thrusters" };

export function upgradeCost(level: number): number | null {
  return level >= P.upgradeCosts.length ? null : P.upgradeCosts[level];
}

export interface BoxReward {
  kind: "coins" | "keys" | "board" | "headstart" | "booster" | "shield" | "jackpot";
  amount: number;
}

/** The Mystery Box tables ⚠ shaped after the wiki's (most boxes are SOL; keys and the jackpot are rare). */
export function openBox(kind: "mystery" | "super", random: () => number = Math.random): BoxReward {
  const r = random();
  if (kind === "super") {
    if (r < 0.0008) return { kind: "jackpot", amount: 300000 };
    if (r < 0.06) return { kind: "keys", amount: 1 + Math.floor(random() * 3) };
    if (r < 0.14) return { kind: "board", amount: 2 + Math.floor(random() * 3) };
    if (r < 0.22) return { kind: "headstart", amount: 2 };
    if (r < 0.30) return { kind: "booster", amount: 2 };
    if (r < 0.36) return { kind: "shield", amount: 2 };
    if (r < 0.40) return { kind: "coins", amount: 15000 + Math.floor(random() * 35000) };
    return { kind: "coins", amount: 1000 + Math.floor(random() * 7000) };
  }
  if (r < 0.0001) return { kind: "jackpot", amount: 100000 };
  if (r < 0.04) return { kind: "keys", amount: 1 };
  if (r < 0.21) return { kind: "board", amount: 1 + Math.floor(random() * 2) };
  if (r < 0.29) return { kind: "headstart", amount: 1 };
  if (r < 0.37) return { kind: "booster", amount: 1 };
  if (r < 0.42) return { kind: "shield", amount: 1 };
  if (r < 0.46) return { kind: "coins", amount: 1500 + Math.floor(random() * 3500) };
  return { kind: "coins", amount: 300 + Math.floor(random() * 700) };
}

export function applyReward(save: SaveData, reward: BoxReward): void {
  switch (reward.kind) {
    case "coins": case "jackpot": save.totalCoins += reward.amount; break;
    case "keys": save.keys += reward.amount; break;
    case "board": save.inventory.boards += reward.amount; save.boards = save.inventory.boards; break;
    case "headstart": save.inventory.headstarts += reward.amount; break;
    case "booster": save.inventory.boosters += reward.amount; break;
    case "shield": save.inventory.shields += reward.amount; break;
  }
}

export function rewardText(reward: BoxReward): string {
  switch (reward.kind) {
    case "coins": return `◎ ${reward.amount.toLocaleString("en-US")} SOL`;
    case "jackpot": return `JACKPOT · ◎ ${reward.amount.toLocaleString("en-US")} SOL`;
    case "keys": return `⚿ ${reward.amount} KEY${reward.amount > 1 ? "S" : ""}`;
    case "board": return `◈ ${reward.amount} HOVER BOARD${reward.amount > 1 ? "S" : ""}`;
    case "headstart": return `${reward.amount} HEADSTART${reward.amount > 1 ? "S" : ""}`;
    case "booster": return `${reward.amount} SCORE BOOSTER${reward.amount > 1 ? "S" : ""}`;
    case "shield": return `${reward.amount} SHIELD${reward.amount > 1 ? "S" : ""}`;
  }
}
